import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import useDeepMemo from "../../../hooks/useDeepMemo";
import useQuery from "../../../hooks/useQuery";
import useSafeState from "../../../hooks/useSafeState";

import { getDistinctValues, toFixed, toFixedValue, isEmpty, isTrue, getStyleColor } from "../../../utils/helpers";
import { useDeepEffect, useSetting, useUnit } from "../../../hooks";
import { DEFAULT_SEPARATOR, LotType, UOM } from "../../../constants";
import { getCutOrderRows } from "../cutPlanHelper";
import SettingKeys from "../../../utils/SettingKeys";
import { convert } from "../../../helpers/unitConversionHelper";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

//** Tab Level 1 = Flow Tab: Flow on Product and Part **//
//** Tab Level 2 = Replan Tab: If Flow is replanned **//

const useView = props => {
  const { match } = props;
  const { orderId, cutOrderId: routeCutOrderId } = match?.params || {};

  //** States **//
  const [version, setVersion] = useSafeState(0);
  const [cutOrderId, setCutOrderId] = useSafeState(0);
  const [flowTabIdx, setFlowTabIdx] = useSafeState(null); // For Multi-Product and Multi-Part
  const [planTabIdx, setPlanTabIdx] = useSafeState(0); // for First Plan and RePlan
  const [cutOrderList, setCutOrderList] = useSafeState([]); // List of Cut Orders for Marrying if required

  const history = useHistory();
  const { allUnitList } = useUnit();

  const tasks = useSelector(state => state.scheduledTask?.data || {});
  const taskList = Object.keys(tasks).map(id => tasks[id]);
  const cutOrderIds = cutOrderList?.map(of => of.id) || [];
  const scheduledTasks = taskList
    .filter(t => cutOrderIds.includes(t.flowId))
    .sort((a, b) => a.modifiedAt - b.modifiedAt)
    .reduce((acc, t) => ({ ...acc, [t.flowId]: t }), {});
  const taskState = Object.values(scheduledTasks).map(t => `${t.id}-${t.state}`);

  //** API calls **//
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const conversionFactor = useSetting(SettingKeys.UOM_CONVERSION_FACTOR);
  const conversionFactorList = JSON.parse(conversionFactor || "[]");

  const { data: order } = useQuery({ url: `orders/${orderId}`, params: { page: "cutPlan" }, version, taskState });
  const { data: ledgerData = [] } = useQuery(orderId && { url: `ledgers`, params: { search: `order.id==${orderId}`, size: 10000 }, version });

  const fabricItemIds = getDistinctValues(ledgerData.map(l => l.fabricItem?.id).filter(id => !isEmpty(id)));

  const fabricPatternIds = getDistinctValues(order?.orderFlowList?.map(flow => flow?.orderConfig?.fabricPatternId).filter(id => !isEmpty(id)));
  const { data: fabricPatternList = {} } = useQuery(
    !isEmpty(fabricPatternIds) && { url: `fabric-patterns`, params: { search: `id=in=(${fabricPatternIds.join(",")})`, size: 100000 } }
  );

  const { data: rollInfoList = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: `rolls`,
      params: { search: `fabricItem.id=in=(${fabricItemIds.join(",")});parentId=='NULL'`, size: 100000 },
      version
    }
  );

  useDeepEffect(() => {
    setCutOrderId(Number(routeCutOrderId));
  }, [routeCutOrderId]);

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(order?.orderFlowList) || isEmpty(routeCutOrderId)) return [];
    const cutOrder = order.orderFlowList.find(of => of.id === Number(routeCutOrderId));
    if (isEmpty(cutOrder)) return [];
    let rowList = getCutOrderRows(order, productParts, null, cutOrder, "cutPlan");

    rowList = rowList
      .map(row => {
        const cutOrder = row.cutOrder;
        const styleColorList = cutOrder?.styleColorList || [];
        const ledgerList = ledgerData
          .filter(
            l =>
              cutOrder.productIdList?.includes(l.productId) &&
              styleColorList.includes(getStyleColor(l.style, l.styleColor)) &&
              l.part === cutOrder.part
          )
          .filter(l => isEmpty(cutOrder.parentColor) || cutOrder.parentColor === l.parentStyleColor);
        const ledger = !isEmpty(ledgerList) ? ledgerList.find(e => e.style === row.style && e.styleColor === row.color) : {};

        const allocatedRollList = rollInfoList.filter(ri => ri.cutOrderFlowId === cutOrder?.id);

        const orderQty = row?.orderQty || 0;
        const fpoQty = row?.fpoQty || 0;
        const uom = row?.uom;
        const ocu = row?.ocu || 0;
        const bomCuList = JSON.parse(cutOrder?.bomCu || "[]");
        const fBomCuList = bomCuList.filter(e => row.styleColorList?.includes(`${e.style}${DEFAULT_SEPARATOR}${e.color}`));
        const bomCu = toFixedValue(fBomCuList.reduce((acc, e) => acc + e.bomCu, 0) / fBomCuList.length, 3);
        // const bomCu = bomCuList?.find(e => e.style === row.style && e.color === row.color)?.bomCu;

        const allocatedLength = allocatedRollList.reduce((acc, ri) => acc + ri.length, 0);
        const allocatedWeight = allocatedRollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const allocatedFabric =
          uom === UOM.KILOGRAM.value
            ? allocatedWeight
            : convert({ input: allocatedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });

        const expOrderQty = ocu > 0 && allocatedFabric > 0 ? Number(toFixed(allocatedFabric / ocu, 0)) : null;
        const expCutPercent = orderQty > 0 && expOrderQty > 0 ? Number(toFixed((expOrderQty * 100) / orderQty, 1)) : null;

        const allocated = isEmpty(row.parentKey) ? toFixed(allocatedFabric) : null;

        return {
          ...row,
          fpoQty,
          itemCode: ledger?.fabric?.itemCode,
          fabColor: ledger?.fabColor,
          fabricId: ledger?.fabric?.id,
          allocatedRollList,
          allocated,
          allocatedLength,
          bomCu,
          expOrderQty: !isEmpty(allocated) ? expOrderQty : null,
          expCutPercent: !isEmpty(allocated) ? expCutPercent : null,
          status: !isEmpty(allocated) ? cutOrder.state : null
        };
      })
      .map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
    return rowList;
  }, [order, routeCutOrderId, ledgerData, rollInfoList, productParts]);

  const flowInfoColumns = useDeepMemo(() => {
    const cutOrder = order?.orderFlowList?.find(of => of.id === Number(routeCutOrderId));
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value },
      { dataKey: "fpo", title: "FPO" },
      { dataKey: "externalFpo", title: "Ext. FPO" },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "productName", title: "Product" },
      { dataKey: "style", title: "Style" },
      { dataKey: "fit", title: "Fit" },
      { dataKey: "color", title: "Style Color" },
      { dataKey: "effPartName", title: "Part" },
      { dataKey: "lotValue", title: "Lot" },
      { dataKey: "orderQty", title: "Order Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "fpoQty", title: "FPO Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "inseam", title: "Inseam" },
      { dataKey: "destination", title: "Destination" },
      { dataKey: "deliveryDate", title: "Delivery Date" },
      {
        dataKey: "itemCode",
        title: "Item Code",
        linkPath: row => history.push(`/warehouse/fabrics/${row?.fabricId}/view`)
      },
      { dataKey: "fabColor", title: "Fabric Color" },
      {
        dataKey: "allocated",
        title: "Allocated \nFabric",
        align: "right",
        render: (value, _, row) =>
          !isEmpty(value) && value > 0
            ? `${toFixed(row.allocatedLength || 0, 0)} m  ${row.uom !== UOM.METER ? `(${value} ${UomCodeFromValue[row.uom]})` : ""}`
            : "-"
      },
      {
        dataKey: "ocu",
        title: "BOM CU",
        render: (value, _, row) =>
          !isEmpty(value) && value > 0
            ? `${toFixedValue(row.bomCu || 0, 3)} m  ${row.uom !== UOM.METER ? `(${toFixedValue(value, 3)} ${UomCodeFromValue[row.uom]})` : ""}`
            : ""
      },
      { dataKey: "expOrderQty", title: "Exp. Order Qty", render: (value, _, row) => (isEmpty(value) ? "" : `${value} (${row.expCutPercent}%)`) },
      { dataKey: "status", title: "Status" }
    ];

    if (flowInfoRows.every(e => isEmpty(e.externalFpo))) {
      cols = cols.filter(c => c.dataKey !== "externalFpo");
    }
    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (cutOrder?.lotType === LotType.PRE_RUN) {
      cols = cols.filter(c => !["allocated", "expOrderQty"].includes(c.dataKey));
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => !["unitId", "fpo", "externalFpo", "fpoQty"].includes(c.dataKey));
    }

    return cols;
  }, [flowInfoRows, order, routeCutOrderId, allUnitList]);

  const cutOrder = flowInfoRows.find(e => e.cutOrder?.id === cutOrderId)?.cutOrder;

  useDeepEffect(() => {
    if (isEmpty(flowInfoRows) || isEmpty(cutOrder)) return;

    const cutOrderList = flowInfoRows
      .filter(e => !isEmpty(e.cutOrder) && isEmpty(e.parentKey))
      .map(e => e.cutOrder)
      .filter(cOrder => {
        //* Set Product *//
        if (!isEmpty(cutOrder?.parentColor)) {
          return (
            cOrder.productParentId === cutOrder?.productParentId &&
            cOrder.styles === cutOrder.styles &&
            cOrder.parentColor === cutOrder.parentColor &&
            cOrder.lot === cutOrder.lot
          );
        }
        //* Non Set Product *//
        else {
          return (
            cOrder.productIdList?.every(id => cutOrder.productIdList?.includes(id)) &&
            cOrder.styles === cutOrder.styles &&
            cOrder.colors === cutOrder.colors &&
            cOrder.lot === cutOrder.lot
          );
        }
      });
    setCutOrderList(cutOrderList);
  }, [flowInfoRows, cutOrder]);

  useDeepEffect(() => {
    const idx = cutOrderList?.findIndex(e => e.id === cutOrder?.id);
    if (!isEmpty(idx) && idx !== -1) {
      setFlowTabIdx(idx);
    }
  }, [cutOrder, cutOrderList]);

  //** Tab Level 1 = Flow Tab: Flow on Product and Part **//
  const flowTabs = useDeepMemo(() => {
    if (isEmpty(cutOrderList) || isEmpty(order?.flowInfoList)) return [];
    let flowTabs = []; // {flowId, label, product, part}
    let flowLabels = [];
    cutOrderList.forEach(e => {
      const productName = e.productIdList.map(id => order.flowInfoList.find(fi => fi.product?.id === id)?.product?.name || "").join(", ");
      const label = `${productName} -> ${e.part}`;
      if (!flowLabels.includes(label)) {
        flowTabs.push({ flowId: e.id, label, product: e.product, part: e.part });
        flowLabels.push(label);
      }
    });
    return flowTabs;
  }, [order, cutOrderList]);

  const ledgerList = useDeepMemo(() => {
    if (isEmpty(ledgerData) || isEmpty(cutOrder)) return [];
    return ledgerData.filter(
      l =>
        cutOrder.productIdList?.includes(l.productId) &&
        cutOrder?.styleColorList?.includes(getStyleColor(l.style, l.styleColor)) &&
        l.part === cutOrder?.part
    );
  }, [ledgerData, cutOrder]);

  const rollList = rollInfoList?.filter(ri => !isEmpty(ri.cutOrderFlowId) && ri.cutOrderFlowId === cutOrder?.id) || [];

  //** Tab Level 3 = Plan Tab: If Flow is replanned **//
  const planTabs = isTrue(cutOrder?.reGenerated) ? ["First Cut Plan", "Re-Planned Cut Plan"] : ["First Cut Plan"];
  //   const planTab = planTabs[planTabIdx];

  // useDeepEffect(() => {
  //   if (isEmpty(flowTabs) || isEmpty(flowId)) return;
  //   const idx = flowTabs.findIndex(e => e.flowId === flowId);
  //   if (idx !== -1) {
  //     setFlowTabIdx(idx);
  //   }
  // }, [flowTabs, flowId]);

  const onTabChange = (name, idx) => {
    if (name === "flow") {
      setFlowTabIdx(idx);
      if (!isEmpty(cutOrderList[idx])) {
        setCutOrderId(cutOrderList[idx].id);
      }

      setPlanTabIdx(0);
    } else if (name === "plan") {
      setPlanTabIdx(idx);
    }
  };

  const onRefresh = () => {
    setVersion(version => version + 1);
  };

  return {
    version,
    order,
    cutOrderId,
    flowInfoColumns,
    flowInfoRows,
    cutOrder,
    cutOrderList,
    scheduledTasks,
    ledgerList,
    fabricPatternList,
    rollList,
    flowTabs,
    planTabs,
    flowTabIdx,
    planTabIdx,
    setCutOrderId,
    onTabChange,
    onRefresh,
    unitList: allUnitList
  };
};

export default useView;
